// utils/geometryProcessing/CalcDraftAngles.js
import * as THREE from 'three';

/**
 * 斜度角分析模块 - 计算和可视化3D模型的斜度角
 */
export class CalcDraftAngles {
  /**
   * 分析网格的斜度角并返回分析结果
   * @param {THREE.Mesh} mesh - 要分析的3D网格
   * @param {THREE.Vector3} draftDirection - 脱模方向向量 (默认为Z轴向上)
   * @param {Object} options - 分析选项
   * @param {boolean} options.visualize - 是否直接修改网格以可视化结果 (默认true)
   * @param {boolean} options.colorCodeVertices - 是否对顶点而非面进行颜色编码 (默认false)
   * @returns {Object} 包含分析结果的对象
   */
  static analyze(mesh, draftDirection = new THREE.Vector3(0, 0, 1), options = { visualize: true, colorCodeVertices: false }) {
    if (!mesh || !mesh.geometry) {
      console.warn('CalcDraftAngles: 无法分析斜度角 - 网格或几何体不可用');
      return null;
    }

    try {
      const geometry = mesh.geometry;
      
      // 确保几何体有正确计算的法线
      geometry.computeVertexNormals();

      const positionAttribute = geometry.getAttribute('position');
      const normalAttribute = geometry.getAttribute('normal');

      if (!positionAttribute || !normalAttribute) {
        console.warn('CalcDraftAngles: 几何体缺少位置或法线属性');
        return null;
      }

      // 标准化脱模方向
      const direction = draftDirection.clone().normalize();

      // 为网格创建颜色属性
      const colorAttribute = new THREE.BufferAttribute(
        new Float32Array(positionAttribute.count * 3),
        3
      );

      // 斜度角分析统计
      const statistics = {
        total: positionAttribute.count / 3, // 面的数量
        ranges: {
          excellent: 0, // 0-10° (绿色) - 很容易脱模
          good: 0,      // 10-20° (浅绿色) - 容易脱模
          moderate: 0,  // 20-30° (黄色) - 中等难度
          challenging: 0, // 30-45° (橙色) - 有挑战
          difficult: 0, // 45-60° (红色) - 困难
          severe: 0,    // 60-80° (深红色) - 非常困难
          impossible: 0 // 80-90° (蓝色) - 几乎不可能脱模
        },
        // 存储每个角度范围的面索引
        facesByRange: {
          excellent: [],
          good: [],
          moderate: [],
          challenging: [],
          difficult: [],
          severe: [],
          impossible: []
        },
        // 记录最小和最大角度
        minAngle: 90,
        maxAngle: 0,
        // 计算平均角度
        totalAngle: 0
      };

      // 对每个面进行分析
      for (let i = 0; i < positionAttribute.count; i += 3) {
        // 获取面的法线 (对于三个顶点使用相同的法线)
        const normal = new THREE.Vector3(
          normalAttribute.getX(i),
          normalAttribute.getY(i),
          normalAttribute.getZ(i)
        );

        // 计算法线与脱模方向的夹角
        const angle = Math.acos(normal.dot(direction));
        const angleDegrees = THREE.MathUtils.radToDeg(angle);

        // 更新统计信息
        statistics.totalAngle += angleDegrees;
        statistics.minAngle = Math.min(statistics.minAngle, angleDegrees);
        statistics.maxAngle = Math.max(statistics.maxAngle, angleDegrees);

        // 根据角度范围分类并分配颜色
        let color;
        let range;

        if (angleDegrees < 10) {
          color = new THREE.Color(0x00ff00); // 亮绿色
          range = 'excellent';
        } else if (angleDegrees < 20) {
          color = new THREE.Color(0x88ff00); // 浅绿色
          range = 'good';
        } else if (angleDegrees < 30) {
          color = new THREE.Color(0xffff00); // 黄色
          range = 'moderate';
        } else if (angleDegrees < 45) {
          color = new THREE.Color(0xffaa00); // 橙色
          range = 'challenging';
        } else if (angleDegrees < 60) {
          color = new THREE.Color(0xff5500); // 红色
          range = 'difficult';
        } else if (angleDegrees < 80) {
          color = new THREE.Color(0xff0000); // 深红色
          range = 'severe';
        } else {
          color = new THREE.Color(0x0000ff); // 蓝色
          range = 'impossible';
        }

        // 增加对应范围的计数
        statistics.ranges[range]++;

        // 记录该面的索引
        const faceIndex = Math.floor(i / 3);
        statistics.facesByRange[range].push(faceIndex);

        // 为该面的所有顶点应用相同的颜色
        colorAttribute.setXYZ(i, color.r, color.g, color.b);
        colorAttribute.setXYZ(i + 1, color.r, color.g, color.b);
        colorAttribute.setXYZ(i + 2, color.r, color.g, color.b);
      }

      // 计算平均角度
      statistics.averageAngle = statistics.totalAngle / statistics.total;

      // 计算总体评分
      const score = this._calculateOverallScore(statistics);

      // 找出问题区域
      const problemAreas = this._identifyProblemAreas(geometry, statistics);

      // 可视化结果
      if (options.visualize) {
        geometry.setAttribute('color', colorAttribute);
        // 创建新材质或修改现有材质以显示顶点颜色
        mesh.material = new THREE.MeshPhongMaterial({
          vertexColors: true,
          side: THREE.DoubleSide,
          flatShading: !options.colorCodeVertices, // 使用colorCodeVertices选项决定是否平滑着色
          shininess: 0
        });
      }

      // 返回完整分析结果
      return {
        statistics,
        score,
        problemAreas,
        mesh: options.visualize ? mesh : null
      };
    } catch (err) {
      console.error('CalcDraftAngles: 分析斜度角时出错:', err);
      return null;
    }
  }

  /**
   * 在指定区域高亮显示斜度角问题
   * @param {THREE.Mesh} mesh - 要高亮的网格
   * @param {Array} problemFaces - 问题面的索引数组
   * @param {Object} options - 高亮选项
   * @returns {THREE.Mesh} 高亮区域的网格
   */
  static highlightProblemAreas(mesh, problemFaces, options = { color: 0xff0000, opacity: 0.7 }) {
    if (!mesh || !mesh.geometry || !problemFaces || !problemFaces.length) {
      return null;
    }

    try {
      // 克隆原始几何体以创建高亮区域
      const highlightGeometry = new THREE.BufferGeometry();
      
      // 获取位置属性
      const positions = mesh.geometry.getAttribute('position');
      const indices = mesh.geometry.index;
      
      const highlightPositions = [];
      
      // 对索引和非索引几何体使用不同方法
      if (indices) {
        // 索引几何体
        for (let i = 0; i < problemFaces.length; i++) {
          const faceIdx = problemFaces[i];
          const idx1 = indices.getX(faceIdx * 3);
          const idx2 = indices.getX(faceIdx * 3 + 1);
          const idx3 = indices.getX(faceIdx * 3 + 2);
          
          highlightPositions.push(
            positions.getX(idx1), positions.getY(idx1), positions.getZ(idx1),
            positions.getX(idx2), positions.getY(idx2), positions.getZ(idx2),
            positions.getX(idx3), positions.getY(idx3), positions.getZ(idx3)
          );
        }
      } else {
        // 非索引几何体
        for (let i = 0; i < problemFaces.length; i++) {
          const faceIdx = problemFaces[i];
          const vertIdx = faceIdx * 3;
          
          highlightPositions.push(
            positions.getX(vertIdx), positions.getY(vertIdx), positions.getZ(vertIdx),
            positions.getX(vertIdx + 1), positions.getY(vertIdx + 1), positions.getZ(vertIdx + 1),
            positions.getX(vertIdx + 2), positions.getY(vertIdx + 2), positions.getZ(vertIdx + 2)
          );
        }
      }
      
      // 如果没有找到问题面，返回null
      if (highlightPositions.length === 0) {
        return null;
      }
      
      // 设置高亮几何体的位置
      highlightGeometry.setAttribute(
        'position', 
        new THREE.Float32BufferAttribute(highlightPositions, 3)
      );
      
      // 计算法线
      highlightGeometry.computeVertexNormals();
      
      // 创建高亮材质
      const material = new THREE.MeshPhongMaterial({
        color: options.color,
        transparent: true,
        opacity: options.opacity,
        side: THREE.DoubleSide,
        depthTest: true,
        depthWrite: false
      });
      
      // 创建高亮网格
      const highlightMesh = new THREE.Mesh(highlightGeometry, material);
      
      // 复制原始网格的变换
      highlightMesh.position.copy(mesh.position);
      highlightMesh.rotation.copy(mesh.rotation);
      highlightMesh.scale.copy(mesh.scale);
      
      return highlightMesh;
    } catch (err) {
      console.error('CalcDraftAngles: 高亮问题区域时出错:', err);
      return null;
    }
  }

  /**
   * 创建颜色图例DOM元素
   * @param {string} containerId - 将包含图例的容器元素ID (可选)
   * @returns {HTMLElement} 图例DOM元素
   */
  static createColorLegend(containerId = null) {
    // 创建图例元素
    const legend = document.createElement('div');
    legend.className = 'draft-angle-legend';
    legend.style.cssText = `
      position: absolute;
      bottom: 20px;
      right: 20px;
      background-color: rgba(255, 255, 255, 0.8);
      border-radius: 5px;
      padding: 10px;
      border: 1px solid #ccc;
      font-family: Arial, sans-serif;
      font-size: 12px;
      z-index: 1000;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    `;

    legend.innerHTML = `
      <div style="margin-bottom: 5px; font-weight: bold; border-bottom: 1px solid #ccc; padding-bottom: 5px;">
        Draft Angle Analysis
        <span id="close-legend" style="float: right; cursor: pointer; font-size: 16px;">&times;</span>
      </div>
      <div style="display: flex; align-items: center; margin: 3px 0;">
        <div style="width: 15px; height: 15px; background-color: #00ff00; margin-right: 5px;"></div>
        <span>0-10° - Excellent</span>
      </div>
      <div style="display: flex; align-items: center; margin: 3px 0;">
        <div style="width: 15px; height: 15px; background-color: #88ff00; margin-right: 5px;"></div>
        <span>10-20° - Good</span>
      </div>
      <div style="display: flex; align-items: center; margin: 3px 0;">
        <div style="width: 15px; height: 15px; background-color: #ffff00; margin-right: 5px;"></div>
        <span>20-30° - Moderate</span>
      </div>
      <div style="display: flex; align-items: center; margin: 3px 0;">
        <div style="width: 15px; height: 15px; background-color: #ffaa00; margin-right: 5px;"></div>
        <span>30-45° - Challenging</span>
      </div>
      <div style="display: flex; align-items: center; margin: 3px 0;">
        <div style="width: 15px; height: 15px; background-color: #ff5500; margin-right: 5px;"></div>
        <span>45-60° - Difficult</span>
      </div>
      <div style="display: flex; align-items: center; margin: 3px 0;">
        <div style="width: 15px; height: 15px; background-color: #ff0000; margin-right: 5px;"></div>
        <span>60-80° - Severe</span>
      </div>
      <div style="display: flex; align-items: center; margin: 3px 0;">
        <div style="width: 15px; height: 15px; background-color: #0000ff; margin-right: 5px;"></div>
        <span>80-90° - Impossible</span>
      </div>
    `;

    // 添加关闭按钮功能
    legend.querySelector('#close-legend').addEventListener('click', () => {
      legend.style.display = 'none';
      
      // 创建"显示图例"按钮
      const showButton = document.createElement('button');
      showButton.textContent = 'Show Legend';
      showButton.style.cssText = `
        position: absolute;
        bottom: 20px;
        right: 20px;
        background-color: #3288e6;
        color: white;
        border: none;
        border-radius: 4px;
        padding: 5px 10px;
        cursor: pointer;
        z-index: 1000;
      `;
      
      showButton.addEventListener('click', () => {
        legend.style.display = 'block';
        showButton.remove();
      });
      
      document.body.appendChild(showButton);
    });

    // 如果提供了容器ID，则将图例添加到容器中
    if (containerId && document.getElementById(containerId)) {
      document.getElementById(containerId).appendChild(legend);
    } else {
      document.body.appendChild(legend);
    }

    return legend;
  }

  /**
   * 根据分析结果更新图例
   * @param {HTMLElement} legend - 图例DOM元素
   * @param {Object} statistics - 分析统计数据
   */
  static updateLegendWithStatistics(legend, statistics) {
    if (!legend || !statistics) return;

    const total = statistics.total;
    
    // 计算各范围的百分比
    const percentages = {};
    for (const range in statistics.ranges) {
      percentages[range] = ((statistics.ranges[range] / total) * 100).toFixed(1);
    }

    // 构建包含统计信息的HTML
    const statsHTML = `
      <div style="margin-top: 10px; border-top: 1px solid #ccc; padding-top: 5px;">
        <div style="margin-bottom: 5px;"><b>Statistics:</b></div>
        <div>Excellent: ${percentages.excellent}% (${statistics.ranges.excellent} faces)</div>
        <div>Good: ${percentages.good}% (${statistics.ranges.good} faces)</div>
        <div>Moderate: ${percentages.moderate}% (${statistics.ranges.moderate} faces)</div>
        <div>Challenging: ${percentages.challenging}% (${statistics.ranges.challenging} faces)</div>
        <div>Difficult: ${percentages.difficult}% (${statistics.ranges.difficult} faces)</div>
        <div>Severe: ${percentages.severe}% (${statistics.ranges.severe} faces)</div>
        <div>Impossible: ${percentages.impossible}% (${statistics.ranges.impossible} faces)</div>
        <div style="margin-top: 5px;">Average Angle: ${statistics.averageAngle.toFixed(1)}°</div>
        <div>Min/Max Angle: ${statistics.minAngle.toFixed(1)}° / ${statistics.maxAngle.toFixed(1)}°</div>
      </div>
    `;

    // 添加统计信息到图例中
    legend.insertAdjacentHTML('beforeend', statsHTML);
  }

  /**
   * 计算整体模型的得分评级
   * @private
   * @param {Object} statistics - 分析统计数据
   * @returns {Object} 得分结果 {score: string, details: Object}
   */
  static _calculateOverallScore(statistics) {
    const total = statistics.total;
    const ranges = statistics.ranges;
    
    // 计算问题区域百分比
    const severePercent = ((ranges.severe + ranges.impossible) / total) * 100;
    const problematicPercent = ((ranges.difficult + ranges.severe + ranges.impossible) / total) * 100;
    const easyPercent = ((ranges.excellent + ranges.good) / total) * 100;
    
    // 确定得分等级
    let score, description;
    
    if (severePercent > 15) {
      score = 'poor';
      description = 'High percentage of severe draft angles. Significant modifications required for successful molding.';
    } else if (problematicPercent > 30) {
      score = 'fair';
      description = 'Considerable problematic areas. Model may require multiple modifications before molding.';
    } else if (problematicPercent > 15 || easyPercent < 60) {
      score = 'good';
      description = 'Some problem areas exist. Minor modifications suggested for optimal results.';
    } else {
      score = 'excellent';
      description = 'Very mold-friendly design. Minimal or no modifications needed.';
    }
    
    return {
      score,
      description,
      details: {
        severePercent: severePercent.toFixed(1),
        problematicPercent: problematicPercent.toFixed(1),
        easyPercent: easyPercent.toFixed(1)
      }
    };
  }

  /**
   * 识别模型中的问题区域
   * @private
   * @param {THREE.BufferGeometry} geometry - 模型几何体
   * @param {Object} statistics - 分析统计数据
   * @returns {Array} 问题区域数组，每个包含位置和描述
   */
  static _identifyProblemAreas(geometry, statistics) {
    // 合并所有严重和不可能脱模的面
    const problemFaces = [
      ...statistics.facesByRange.severe,
      ...statistics.facesByRange.impossible
    ];
    
    if (problemFaces.length === 0) {
      return [];
    }
    
    // 获取位置属性
    const positions = geometry.getAttribute('position');
    
    // 计算每个问题面的中心点
    const faceCenters = [];
    for (const faceIdx of problemFaces) {
      const idx = faceIdx * 3;
      
      // 取三个顶点
      const v1 = new THREE.Vector3(
        positions.getX(idx),
        positions.getY(idx),
        positions.getZ(idx)
      );
      
      const v2 = new THREE.Vector3(
        positions.getX(idx + 1),
        positions.getY(idx + 1),
        positions.getZ(idx + 1)
      );
      
      const v3 = new THREE.Vector3(
        positions.getX(idx + 2),
        positions.getY(idx + 2),
        positions.getZ(idx + 2)
      );
      
      // 计算中心点
      const center = new THREE.Vector3()
        .add(v1)
        .add(v2)
        .add(v3)
        .divideScalar(3);
      
      faceCenters.push(center);
    }
    
    // 使用简单聚类来找到主要问题区域
    const problemAreas = this._clusterProblemAreas(faceCenters, problemFaces);
    
    return problemAreas;
  }

  /**
   * 将问题面聚类为主要问题区域
   * @private
   * @param {Array} centers - 面中心点坐标数组
   * @param {Array} faceIndices - 面索引数组
   * @returns {Array} 问题区域数组
   */
  static _clusterProblemAreas(centers, faceIndices) {
    if (centers.length === 0) return [];
    
    // 聚类距离阈值
    const threshold = 5.0;
    
    // 已处理的面索引
    const processed = new Set();
    
    // 结果区域
    const areas = [];
    
    // 简单聚类算法
    for (let i = 0; i < centers.length; i++) {
      if (processed.has(i)) continue;
      
      // 创建新聚类
      const cluster = {
        center: centers[i].clone(),
        faces: [faceIndices[i]],
        position: `x:${centers[i].x.toFixed(1)}, y:${centers[i].y.toFixed(1)}, z:${centers[i].z.toFixed(1)}`
      };
      
      processed.add(i);
      
      // 查找相近的面
      for (let j = 0; j < centers.length; j++) {
        if (processed.has(j)) continue;
        
        if (centers[i].distanceTo(centers[j]) < threshold) {
          cluster.center.add(centers[j]);
          cluster.faces.push(faceIndices[j]);
          processed.add(j);
        }
      }
      
      // 计算最终中心点
      if (cluster.faces.length > 1) {
        cluster.center.divideScalar(cluster.faces.length);
        cluster.position = `x:${cluster.center.x.toFixed(1)}, y:${cluster.center.y.toFixed(1)}, z:${cluster.center.z.toFixed(1)}`;
      }
      
      // 添加描述
      if (cluster.faces.length >= 10) {
        cluster.description = `Major problem area with ${cluster.faces.length} faces having severe draft angles.`;
        cluster.severity = 'high';
      } else if (cluster.faces.length >= 5) {
        cluster.description = `Moderate problem area with ${cluster.faces.length} faces requiring attention.`;
        cluster.severity = 'medium';
      } else {
        cluster.description = `Minor problem area with ${cluster.faces.length} faces that might need adjustment.`;
        cluster.severity = 'low';
      }
      
      areas.push(cluster);
    }
    
    // 按严重性排序
    return areas.sort((a, b) => b.faces.length - a.faces.length);
  }
}